import { createRef } from 'react';
import React, { useEffect, useState } from 'react';
import { Modal } from 'antd';
import type { ModalProps } from 'antd';
import { Button } from 'antd';
import FormattedMessageWrapper from 'components/App/FormattedMessageWrapper';

export interface AppFormInjectedProps {
  closeModal: () => void;
  onSuccess?: (...args: any[]) => void;
  setModalFooter: (footer: React.ReactNode[] | null | undefined) => void;
  setSaveButtonProps: (props: { disabled?: boolean; loading?: boolean; text?: string | React.ReactNode }) => void;
}

export type AppFormType<P> = React.ComponentType<P & AppFormInjectedProps>;

export abstract class AppFormComponent<P = object, S = object> extends React.Component<P & AppFormInjectedProps, S> {
  // Optionnel : une méthode helper pour valider et fermer
  finish = (...args: any[]) => {
    if (this.props.onSuccess) {
      this.props.onSuccess(...args);
    }
    // La fermeture est déjà gérée par le wrapper statique,
    // mais on laisse l'accès à closeModal au cas où.
    this.props.closeModal();
  };

  abstract submit(): void;
}

interface ModalConfig<P = any> {
  Component: AppFormType<P>;
  componentProps: P & AppFormInjectedProps & { onSuccess?: (...args: any[]) => void };
  customFooter?: React.ReactNode[] | null;
  formRef: React.RefObject<any>;
  id: number;
  modalProps: ModalProps;
  saveButtonProps: { disabled?: boolean; loading?: boolean; text?: string | React.ReactNode };
}

type OpenModalFn = <P extends object>(
  Component: AppFormType<P>,
  props: P & { onSuccess?: (...args: any[]) => void },
  modalProps?: ModalProps,
) => void;

let openFn: OpenModalFn;

export const AppForm = ({ children }: { children: React.ReactNode }) => {
  const [modals, setModals] = useState<ModalConfig[]>([]);

  const open: OpenModalFn = (Component, props, modalProps = {}) => {
    const model = (props as { model?: unknown }).model;

    const id = Date.now();
    const formRef = createRef<AppFormComponent>();

    const close = () => setModals((prev) => prev.filter((m) => m.id !== id));

    const setModalFooter = (footer: React.ReactNode[] | null | undefined) => {
      setModals((prev) => prev.map(m =>
        m.id === id ? {
          ...m,
          customFooter: footer,
        } : m,
      ));
    };

    const setSaveButtonProps = (buttonProps: any) => {
      setModals((prev) => prev.map(m =>
        m.id === id ? {
          ...m,
          saveButtonProps: { ...m.saveButtonProps, ...buttonProps },
        } : m,
      ));
    };

    // Surcharge automatique du onSuccess pour fermer la modale
    const originalOnSuccess = props.onSuccess;
    const augmentedProps = {
      ...props,
      closeModal: close,
      onSuccess: (...args: any[]) => {
        if (originalOnSuccess) {
          originalOnSuccess(...args);
        }
        close(); // Fermeture automatique
      },
      setModalFooter,
      setSaveButtonProps,
    };

    setModals((prev) => {
      const alreadyOpen = model !== undefined && prev.some(
        m => m.Component === Component && (m.componentProps as { model?: unknown }).model === model,
      );

      if (alreadyOpen) {
        return prev;
      }

      return [
        ...prev,
        {
          Component,
          componentProps: augmentedProps,
          formRef,
          id,
          modalProps,
          saveButtonProps: {
            disabled: false,
            loading: false,
          },
        },
      ];
    });
  };

  // On attache la fonction à la référence statique lors du montage
  useEffect(() => {
    openFn = open;
  }, []);

  return (
    <>
      {children}
      {modals.map((m) => {
        const {
          Component,
          componentProps,
          customFooter,
          formRef,
          id,
          modalProps,
          saveButtonProps,
        } = m;

        const currentFooter = customFooter !== undefined
          ? customFooter
          : (modalProps.footer !== undefined ? modalProps.footer : [
            <Button
              key="back"
              onClick={() => componentProps.closeModal()}
            >
              <FormattedMessageWrapper id="Annuler" />
            </Button>,
            <Button
              disabled={saveButtonProps.disabled}
              key="submit"
              loading={saveButtonProps.loading}
              onClick={() => {
                Promise.resolve(formRef.current?.submit()).catch(() => {});
              }}
              type="primary"
            >
              {saveButtonProps.text || <FormattedMessageWrapper id="Enregistrer" />}
            </Button>,
          ]);

        return (
          <Modal
            destroyOnHidden
            footer={currentFooter}
            key={id}
            onCancel={() => componentProps.closeModal()}
            open={true}
            width={1024}
            {...modalProps}
          >
            {(
              (Component.prototype && Component.prototype.isReactComponent)
              || (Component as any).$$typeof === Symbol.for('react.forward_ref')
            ) ? (
              <Component {...componentProps} ref={formRef} />
            ) : (
              <Component {...componentProps} />
            )}
          </Modal>
        );
      })}
    </>
  );
};

const openAppForm = <P extends object>(
  Component: AppFormType<P>,
  props: P & { onSuccess?: (...args: any[]) => void },
  modalProps?: ModalProps,
) => {
  if (openFn) {
    openFn(Component, props, modalProps);
  } else {
    console.warn('AppForm: openFn is not initialized. Did you forget to wrap your app with <AppForm>?');
  }
};

AppForm.open = openAppForm;